
module agi.expressions.card.utils{

    
    /**
     * Class describing info about time.
     */
    export class TimeInfo{
        private seconds:number = 0;
        private minutes:number = 0;
        private hours:number = 0;

        public static HOURS = 0x4;
        public static MINUTES = 0x2;
        public static SECONDS = 0x1;

        constructor(seconds:number=0, minutes:number=0, hours:number=0){
            this.setAll(seconds, minutes, hours);
        }

        public setWithSeconds(totalSeconds:number){
            this.seconds = totalSeconds % 60;
            let totalMinutes = Math.floor(totalSeconds / 60);    
            this.minutes = totalMinutes % 60;
            this.hours = Math.floor(totalMinutes / 60);
        }   

        public setWithMinutes(totalMinutes:number){
            this.setWithSeconds(totalMinutes * 60);
        }

        public setWithHours(totalHours:number){
            this.setWithSeconds(totalHours * 60 * 60);
        }

        public setAll(seconds:number, minutes:number, hours:number){
            this.setWithSeconds(seconds + minutes * 60 + hours * 60 * 60);
        }

        /** Get total time in seconds */
        public getTimeInSeconds():number{
            return (this.hours * 60 * 60) + (this.minutes * 60) + this.seconds;
        }
        /** Get total time in minutes */        
        public getTimeInMinutes():number{
            return this.hours * 60 + this.minutes + this.seconds/60.0;
        }
        /** Get total time in hours */        
        public getTimeInHours():number{
            return this.hours + this.minutes/60.0 + this.seconds/60/60;
        }

        /**
         * Get a copy of the time's data.
         */
        public getData() : {seconds:number,minutes:number,hours:number} 
        {
            return {
                seconds:this.seconds,
                minutes:this.minutes, 
                hours:this.hours
            }
        }


        /** Increment the time of this object with seconds */
        public incrementSeconds(seconds:number){
            this.setWithSeconds(this.getTimeInSeconds() + seconds);
        }

        private padZero(num:number) : string{
            if (num < 10)
                return "0" + num;
            else
                return num.toString();
        }

        /**
         * Convert the TimeInfo object to a nicely formatted string
         * @param mask A bitmask representing what to display. Only 3 bits are used, which represent, from left to right, hours minutes seconds
         * @param separator What to separate time units with. Default is ':'
         * @param leadingZero Should we show a zero in front of the largest time unit if it is less than 10?
         * @example
         * //Assume time is a TimeInfo instance
         * time.toString(TimeInfo.HOURS | TimeInfo.MINUTES | TimeInfo.SECONDS); //Prints hours:minutes:seconds. 
         * time.toString(); //Same as above
         * time.toString(TimeInfo.MINUTES | TimeInfo.SECONDS); //Prints minutes:seconds
         * time.toString(TimeInfo.HOURS | TimeInfo.SECONDS); //prints hours:seconds
         */
        public toString(mask:number = 0x7, separator:string=":", leadingZero:boolean=true) : string{
            let out = "";
            if (mask & TimeInfo.HOURS)
            {
                out += leadingZero ? this.padZero(this.hours) : this.hours;
            }
            if (mask & TimeInfo.MINUTES){
                out += separator; 
                if (!(mask & TimeInfo.HOURS))
                    if (leadingZero)
                        out += this.padZero(this.minutes);
                    else
                        out += this.minutes;
                else
                    out += this.padZero(this.minutes);
            }
            if (mask & TimeInfo.SECONDS){
                out += separator; 
                if (!(mask & TimeInfo.HOURS) && !(mask & TimeInfo.MINUTES))
                    if (leadingZero)
                        out += this.padZero(Math.floor(this.seconds));
                    else
                        out += Math.floor(this.seconds);
                else
                    out += this.padZero(Math.floor(this.seconds));
            }

            if (out.charAt(0) == separator)
                out = out.substring(1);
            if (out.charAt(out.length-1) == separator)
                out = out.substring(0, out.length-1);

            return out;
        }
    }

    /**
     * Flexible Stopwatch that can be used with any js project. Call start() to start it.
     * If you'd like to use your own animation loop, call the public update method in your loop, and specifiy that in the constructor
     * @author Dan Singer
     */
    export class Stopwatch{

        private time : TimeInfo;
        private isRunning:boolean = false;
        private lastTimestamp: number = 0;
        private maxTime : TimeInfo;
        private maxDeltaTime : number = 1;

        /**
         * Construct a new Stopwatch.
         * @param haveOwnRenderLoop Do you already have a render loop setup? If true, you'll need to call stopwatch.update(dt) inside of it!
         */
        constructor(haveOwnRenderLoop:boolean, maxTime:TimeInfo=null){
            this.time = new TimeInfo();
            this.maxTime = maxTime;
            if (!haveOwnRenderLoop)
            {
                let loop = (timestamp) => {
                    let dt = (timestamp - this.lastTimestamp) / 1000;
                    if (dt < this.maxDeltaTime)
                        this.update(dt);
                    this.lastTimestamp = timestamp;
                    requestAnimationFrame(loop);
                };
                requestAnimationFrame(loop);
            }
        }

        public start() : void{
            this.isRunning = true;
        }
        
        public stop() : void{
            this.isRunning = false;
        }

        /**
         * Reset the stopwatch to 0 and stop it from running
         */
        public reset() : void{
            this.time.setWithSeconds(0);
            this.isRunning = false;
        }


        public getCurrentTime() : TimeInfo{
            return this.time;
        }
        public getIsRunning(): boolean{
            return this.isRunning;
        }

        /**
         * Update the stopwatch's time
         * @param dt Change in time since the last frame
         */
        public update(dt:number) : void{
            if (this.isRunning)
            {
                this.time.incrementSeconds(dt);
                if (this.maxTime && this.time.getTimeInSeconds() >= this.maxTime.getTimeInSeconds() )
                {
                    this.stop();
                    //Just in case the user left the tab and returned in less than one second, time could have been incremented past maxTime. So put it back to maxTime
                    this.time.setWithSeconds(this.maxTime.getTimeInSeconds());
                }
            }
        }
        
    }
}